import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { NavItem } from "@onemileauto/ui";
import type { Folder } from "@onemileauto/shared-types";
import { useFolders } from "../../hooks/use-folders";
import { iconForFolderSlug } from "../../lib/folder-icons";
import { FolderEditorSheet } from "./folder-editor-sheet";

interface FolderNavProps {
  mailboxId: string | undefined;
  activeSlug: string;
  onSelect: (slug: string) => void;
  /** Owner/full-permission users only (see lib/acl.ts's "folder_admin" level) — read_only/send_as users see the same folder list with no create/manage affordances, rather than a control that would just 404 on submit. */
  canManageFolders?: boolean;
  className?: string;
}

/**
 * The folder tree/nav list — Inbox, Archive, Sent, Drafts, Trash, Spam,
 * plus any custom folders a user has created (Phase 6), all data-driven
 * from GET /folders. Replaces the old horizontal filter-pill row
 * (folder-tabs.tsx): this is a vertical nav list meant to live in a
 * persistent rail (desktop) or a mobile drawer, not a row squeezed into the
 * top of the list pane — see mail-shell.tsx for how it's mounted at each
 * viewport size.
 *
 * Folder management (Phase 6): a custom folder's row gets a small,
 * always-visible "manage" button overlaid on its right edge (same
 * always-visible-not-gesture-dependent pattern thread-list-item.tsx uses
 * for its persistent Archive button) opening FolderEditorSheet to rename or
 * delete it. System folders never render this trigger — enforced again
 * server-side regardless (see MailboxDO#renameFolder/deleteFolder).
 */
export function FolderNav({ mailboxId, activeSlug, onSelect, canManageFolders, className }: FolderNavProps) {
  const { data } = useFolders(mailboxId);
  const folders = data?.folders ?? [];
  const [editingFolder, setEditingFolder] = useState<Folder | undefined>(undefined);
  const [creatingFolder, setCreatingFolder] = useState(false);

  if (folders.length === 0 && !canManageFolders) return null;

  return (
    <>
      <nav aria-label="Folders" className={className}>
        <ul className="flex flex-col gap-0.5">
          {folders.map((folder) => (
            <li key={folder.id} className="relative">
              <NavItem
                icon={iconForFolderSlug(folder.slug)}
                label={folder.name}
                active={folder.slug === activeSlug}
                onClick={() => onSelect(folder.slug)}
                className={!folder.isSystem && canManageFolders ? "pr-11" : undefined}
              />
              {!folder.isSystem && canManageFolders && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingFolder(folder);
                  }}
                  aria-label={`Manage ${folder.name} folder`}
                  className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted hover:bg-parchment-dim hover:text-rust-deep"
                >
                  <Pencil size={15} />
                </button>
              )}
            </li>
          ))}
        </ul>
        {canManageFolders && (
          <button
            type="button"
            onClick={() => setCreatingFolder(true)}
            className="mt-0.5 flex h-11 w-full items-center gap-3 rounded-md px-3 text-left text-[15px] font-medium text-muted transition-colors hover:bg-parchment-dim hover:text-rust-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
          >
            <Plus size={19} strokeWidth={1.75} className="shrink-0" aria-hidden />
            <span>New folder</span>
          </button>
        )}
      </nav>

      {creatingFolder && mailboxId && <FolderEditorSheet mailboxId={mailboxId} onClose={() => setCreatingFolder(false)} />}
      {editingFolder && mailboxId && (
        <FolderEditorSheet mailboxId={mailboxId} folder={editingFolder} onClose={() => setEditingFolder(undefined)} />
      )}
    </>
  );
}
